import { cord, cord3, formatInput, gql2 } from "@/gql/lib";
import { Subject, InvEntityType } from "../data/model";
import { fixTier3XY, correctTier12XY } from "@/gql/cords";
import {
  ActionEnum,
  AxisType,
  TimeAxisRangeInput,
  targetAxis,
} from "@/gql/types";
import { POPULATION_TYPE, PopulationTypeHelper } from "@/common/models";
import { SharedState } from "@/common/state";
import { UseModal } from "@/components/root/modal";
import EntitiesTableModalVue from "@/components/modals/entities-table-modal/EntitiesTableModal.vue";
import { EntitiesModalProps } from "@/components/modals/entities-table-modal/model";
import { TimeFrameOption } from "@/common/state/models";
import { get_entities_modal_target_axis } from "./common";
import { apiCall } from "@/common/api/ApiResult.model";
import {
  getFolderForensicChildren,
  getFolderForensicChildrenCount,
} from "@/common/api/folder-forensic.api";

export const get_sub_items_count = apiCall<string, { count: number }>(
  "entity-investigation.sub_folders_count",
  async (folder_id) => {
    const res = await getFolderForensicChildrenCount(folder_id);
    if (res.success) {
      return {
        success: true,
        value: {
          count:
            typeof res.value === "number"
              ? res.value
              : Number(
                  (res.value as unknown as { count?: number })?.count ?? 0
                ),
        },
      };
    }
    throw Object.assign(new Error("Failed to fetch sub folder count"), {
      response: { status: res.code, data: res.body },
    });
  }
);

export const get_sub_folders = apiCall<
  string,
  Array<{ id: string; name: string; path: string }>
>("entity-investigation.sub_folders", async (folder_id) => {
  const res = await getFolderForensicChildren(folder_id);
  if (res.success) {
    const children = Array.isArray(res.value) ? res.value : [];
    return {
      success: true,
      value: children.map(({ id, name, path }) => ({
        id,
        name,
        path,
      })),
    };
  }
  throw Object.assign(new Error("Failed to fetch sub folders"), {
    response: { status: res.code, data: res.body },
  });
});

const summary_sentence = (subject: Subject) => {
  switch (subject.type) {
    case POPULATION_TYPE.EMPLOYEE:
      return `Folders containing files accessible by ${subject.name}`;
    case POPULATION_TYPE.ORGANIZATION:
    case POPULATION_TYPE.PRIVATE_EMAIL:
    case POPULATION_TYPE.CONTACT:
      return `Folders containing files that were sent to ${subject.name}`;
    case POPULATION_TYPE.LABEL:
      return `Folders containing ${subject.name} information`;
    default:
      return "?";
  }
};

const get_summary = gql2<
  { subject: Subject; relative_time_frame: TimeFrameOption },
  { count: number; relative_count: number; type: InvEntityType; desc: string }
>("inv-counters.folders", ({ subject, relative_time_frame }, { frame }) => {
  const cords = (time_frame: TimeAxisRangeInput) =>
    correctTier12XY({
      coordinate: {
        x: cord(AxisType.FolderType),
        y: cord(PopulationTypeHelper.asAxisType2(subject.type, "both"), [
          subject.id,
        ]),
        z: time_frame,
      },
      action: ActionEnum.UniqCount,
    });
  return {
    query: `{
    current: tier1count(params:${formatInput(cords(frame))}){
        count
        uniqs
     }
     relative: tier1count(params:${formatInput(
       cords({
         type: relative_time_frame.type,
         values: [relative_time_frame.from],
       })
     )}){
      count
      uniqs
   }
    }`,
    mapper: (res) => {
      const { relative, current } = res;
      return {
        type: POPULATION_TYPE.FOLDER,
        count: current.length > 0 ? current[0].uniqs : 0,
        relative_count: relative.length > 0 ? relative[0].uniqs : 0,
        desc: summary_sentence(subject),
      };
    },
  };
});

const get_titles = (subject: Subject) => {
  if (subject.type == POPULATION_TYPE.LABEL) {
    const label_name = subject.name.replace(/information/i, "");
    return {
      main: `<b>Folders</b> containing <b>${label_name}</b> information`,
      events: (name: string) =>
        `Events involving <b>${label_name}</b> information stored in folder <b>${name}</b>`,
      files: (name: string) =>
        `<b>${label_name}</b> information files stored in folder <b>${name}</b>`,
    };
  } else {
    return {
      main: `<b>Folders</b> containing files accessible by <b>${subject.name}</b>`,
      events: (name: string) =>
        `Events where <b>${subject.name}</b> sent/received files from the <b>${name}</b> folder`,
      files: (name: string) =>
        `Files accessible by <b>${subject.name}</b> in the <b>${name}</b> folder`,
    };
  }
};

const _get_main_cords = (subject: Subject) => {
  const { from, type } = SharedState.selectedTimeFrameOption.value;
  return correctTier12XY({
    coordinate: {
      x: cord(AxisType.Folder),
      y: cord(PopulationTypeHelper.asAxisType2(subject.type), [subject.id]),
      z: {
        type,
        values: [from],
      },
    },
  }).coordinate;
};

const _get_events_cords = (subject: Subject, sub_subject_id: string) => {
  const { from, type } = SharedState.selectedTimeFrameOption.value;
  return fixTier3XY({
    x: cord3(AxisType.Folder, [sub_subject_id]),
    y: cord3(PopulationTypeHelper.asAxisType2(subject.type), [subject.id]),
    z: {
      type,
      values: [from],
    },
  });
};

const show_modal = async (subject: Subject) => {
  const titles = get_titles(subject);
  const cord = _get_main_cords(subject);
  const props: EntitiesModalProps = {
    title: titles.main,
    type: POPULATION_TYPE.FOLDER,
    info_types: [],
    target_axis: get_entities_modal_target_axis(subject.id, cord),
    coordinate: cord,
    files_title: (entity) => titles.files(entity.name),
    events: {
      coordinate: (entity) => {
        return _get_events_cords(subject, entity.id);
      },
      title: (entity) => titles.events(entity.name),
    },
  };
  UseModal().show(EntitiesTableModalVue, props);
};

export default { get_summary, show_modal };
